"use client";

import { X, ChevronRight, Waves, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import Cookies from "js-cookie";
import { getRol } from "@/lib/Auth";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";

interface WelcomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function WelcomeModal({
  open,
  onOpenChange,
}: WelcomeModalProps): JSX.Element {
  const router = useRouter();
  const [perfilUrl, setPerfilUrl] = useState('/perfil/usuario');
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const getPerfilUrl = async () => {
      const rol = await getRol();
      switch (rol) {
        case "ROLE_ESCUELA":
          setPerfilUrl('/perfil');
          break;
        case "ROLE_INSTRUCTOR":
          setPerfilUrl('/perfil');
          break;
        default:
          setPerfilUrl('/perfil');
      }
    };
    getPerfilUrl();

    // Cambiar automáticamente la sección activa cada 3 segundos
    const interval = setInterval(() => {
      setActiveSection((prev) => (prev + 1) % 3);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Variantes para animaciones
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.4, 
        ease: [0.22, 1, 0.36, 1], // Curva de animación personalizada
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      transition: { duration: 0.3, ease: "easeIn" }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { 
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      } 
    }
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      repeatType: "reverse" as const,
      ease: "easeInOut"
    }
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
      duration: 3,
      repeat: Infinity,
      repeatType: "reverse" as const,
      ease: "easeInOut"
    }
  };

  const rotateAnimation = {
    rotate: [0, 360],
    transition: {
      duration: 20,
      repeat: Infinity,
      ease: "linear"
    }
  };

  const slideTexts = [
    "Conecta con la comunidad",
    "Encuentra las mejores olas",
    "Comparte tu pasión"
  ];

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="max-w-4xl  overflow-hidden z-[9999] border-none shadow-2xl rounded-2xl bg-white"
      style={{ 
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 40px rgba(0, 0, 0, 0.1) inset"
      }}
    >
      <div className="grid md:grid-cols-2">
        <div className="relative h-[350px] md:h-auto overflow-hidden">
          {/* Fondo con efecto de paralaje */}
          <motion.div
            animate={{ y: [-5, 5, -5] }}
            transition={{ 
              duration: 10, 
              repeat: Infinity, 
              repeatType: "reverse", 
              ease: "easeInOut" 
            }}
            className="absolute inset-0 z-0"
          >
            <Image
              src="/images/backgrounds/sunset.png"
              alt="Surfers at sunset"
              className="object-cover scale-110"
              fill
              priority
            />
          </motion.div>
          
          {/* Overlay con gradiente mejorado */}
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-800/50 to-transparent z-10"></div>
          
          {/* Elementos decorativos animados */}
          <motion.div 
            animate={rotateAnimation}
            className="absolute top-1/4 right-1/4 w-40 h-40 rounded-full border border-white/10 z-20"
          />
          <motion.div 
            animate={rotateAnimation}
            className="absolute top-1/4 right-1/4 w-60 h-60 rounded-full border border-white/10 z-20"
          />
          <motion.div 
            animate={rotateAnimation}
            className="absolute top-1/4 right-1/4 w-80 h-80 rounded-full border border-white/10 z-20"
          />
          
          {/* Elementos flotantes */}
          <motion.div 
            animate={floatingAnimation}
            className="absolute top-10 right-10 z-20"
          >
            <div className="w-12 h-12 rounded-full bg-blue-400/20 backdrop-blur-md flex items-center justify-center">
              <Waves className="h-6 w-6 text-white/80" />
            </div>
          </motion.div>
          
          <motion.div 
            animate={pulseAnimation}
            className="absolute top-1/3 left-10 z-20"
          >
            <div className="w-8 h-8 rounded-full bg-indigo-400/20 backdrop-blur-md flex items-center justify-center">
              <Sun className="h-4 w-4 text-white/80" />
            </div>
          </motion.div>
          
          {/* Texto principal con animación */}
          <div className="absolute bottom-0 left-0 right-0 p-8 z-30">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
            >
              <h3 className="font-black text-4xl tracking-tight drop-shadow-lg text-white">
                SURFWAY
              </h3>
              
              <div className="h-10 relative overflow-hidden mt-1">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={activeSection}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-sm font-medium text-blue-100 drop-shadow-md absolute"
                  >
                    {slideTexts[activeSection]}
                  </motion.p>
                </AnimatePresence>
              </div>
              
              {/* Indicadores de slide */}
              <div className="flex space-x-1 mt-3">
                {[0, 1, 2].map((idx) => (
                  <motion.div
                    key={idx}
                    className={`h-1 rounded-full ${idx === activeSection ? 'w-6 bg-white' : 'w-2 bg-white/40'}`}
                    animate={{ width: idx === activeSection ? 24 : 8 }}
                    transition={{ duration: 0.3 }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="p-10 relative bg-gradient-to-br from-white via-blue-50 to-indigo-100">
          {/* Elementos decorativos de fondo mejorados */}
          <div className="absolute top-0 right-0 w-60 h-60 bg-gradient-to-br from-blue-300/10 to-indigo-300/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-gradient-to-tr from-blue-300/10 to-indigo-300/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>
          <motion.div 
            animate={{
              opacity: [0.5, 0.8, 0.5],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-gradient-to-r from-blue-400/5 to-indigo-400/5 rounded-full blur-3xl"
          />
          
         {/*  <button
            onClick={() => onOpenChange(false)}
            className="absolute right-5 top-5 rounded-full p-2.5 bg-white/90 backdrop-blur-sm shadow-sm transition-all hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 z-10"
          >
            <X className="h-4 w-4 text-gray-700" />
            <span className="sr-only">Cerrar</span>
          </button> */}
          
          <div className="relative z-10">
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl font-black tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent">
                ¡BIENVENIDO A LA FAMILIA SURFWAY!
              </h2>
              
              <div className="w-20 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mt-3"></div>
              
              <motion.p 
                variants={itemVariants}
                className="mt-6 text-md text-gray-700 leading-relaxed"
              >
                Hasta ahora, tus datos solo pueden ser vistos por los
                administradores y vos. ¡Estás a un solo paso de que miles de
                personas en todo el mundo puedan conocerte!
              </motion.p>
              
              <motion.div 
                variants={itemVariants}
                className="mt-5 p-5 bg-gradient-to-r from-blue-100/80 to-indigo-100/80 rounded-xl border border-blue-200 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Waves className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    Solo te falta ingresar a tu perfil y suscribirte. Si tenés un pase Free, 
                    contactá a nuestro equipo para que puedan habilitar tu perfil.
                  </p>
                </div>
              </motion.div>
            </motion.div>
            
            <motion.div 
              variants={itemVariants}
              className="mt-8 space-y-4"
            >
              <Link href={perfilUrl}>
                <Button className="w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600 hover:from-blue-700 hover:via-indigo-600 hover:to-blue-700 text-white font-semibold py-6 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] group">
                  <span>IR A MI PERFIL</span>
                  <motion.div
                    className="ml-2"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ 
                      duration: 1.5, 
                      repeat: Infinity, 
                      repeatType: "reverse" 
                    }}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </motion.div>
                </Button>
              </Link>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  onOpenChange(false);
                  router.push('/');
                }}
                className="w-full text-gray-600 hover:text-gray-900 font-medium py-3 transition-colors text-sm flex items-center justify-center gap-1 group"
              >
                <span>Cerrar por ahora</span>
                <span className="inline-block transition-transform group-hover:translate-x-1">&rarr;</span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
